import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiCheck } from 'react-icons/fi';
import { Footer, SEO } from '@/components';
import { DivorceFitCheck } from '@/components/divorce/DivorceFitCheck';
import { DivorceAccountGate } from '@/components/divorce/DivorceAccountGate';
import { DivorcePhoneActivation } from '@/components/divorce/DivorcePhoneActivation';
import { SuggestInput } from '@/components/divorce/SuggestInput';
import {
  suggestCities,
  suggestCounties,
  suggestStateCountry,
} from '@/lib/divorce/places';
import shipSvg from '@/assets/ship.svg';
import { WizardShell } from '@/components/parenting-plan';
import { Input } from '@/components/Input';
import { API_BASE_URL, apiFetch } from '@/lib/api';
import { track } from '@/lib/analytics';
import { requireDivorceProduct } from '@/lib/divorce-gate';
import { DIVORCE_JURISDICTIONS, isPacketState } from '@/lib/divorce';
import { DV_SCREEN_HINT, DV_SCREEN_QUESTION } from '@/lib/divorce-legal';

/* ------------------------------------------------------------------ types */

type Party = {
  name?: string;
  former_name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
};

type Child = {
  name?: string;
  dob?: string;
  lives_with?: 'petitioner' | 'respondent' | 'both' | 'other';
};

export type DivorceAnswers = {
  filing_mode?: 'sole' | 'joint';
  petitioner?: Party;
  respondent?: Party;
  marriage?: {
    date?: string;
    city?: string;
    state?: string;
    separation_date?: string;
    common_law?: boolean;
  };
  residency?: {
    months_in_state?: number;
    months_in_county?: number;
    county?: string;
    military?: boolean;
  };
  children?: Child[];
  property?: {
    has_real_estate?: boolean;
    has_retirement?: boolean;
    has_business?: boolean;
  };
  debts?: { description?: string; responsible?: string }[];
  support?: {
    child_support_requested?: boolean;
    spousal_support_requested?: boolean;
  };
  agreement?: {
    schedule_slug?: string;
    parenting_plan_claim_token?: string;
    holidays?: string;
    decision_making?: 'joint' | 'petitioner' | 'respondent';
    expense_split?: string;
  };
  service?: {
    spouse_will_sign_waiver?: boolean;
    spouse_address_known?: boolean;
    same_household?: boolean;
  };
  fee_waiver?: {
    requested?: boolean;
    household_size?: number;
    monthly_income_cents?: number;
  };
  eligibility?: Record<string, boolean | undefined>;
  name_change_requested?: boolean;
};

type GateReason = { code: string; message: string; resource_url?: string | null };

type EligibilityResult = {
  status: 'eligible' | 'blocked' | 'wait';
  blocked_category?: string | null;
  reasons: GateReason[];
  warnings: GateReason[];
};

type DraftResponse = {
  claim_token: string;
  jurisdiction_code: string | null;
  answers: DivorceAnswers;
  current_step: string | null;
  email_attached: boolean;
  eligibility: EligibilityResult | null;
  paid: boolean;
  packet_available?: boolean;
  packet_ready?: boolean;
};

/* ------------------------------------------------------------------ steps */

const STEPS = [
  { id: 'state', label: 'Your state' },
  { id: 'eligibility', label: 'Basics' },
  { id: 'parties', label: 'You & your spouse' },
  { id: 'marriage', label: 'Marriage' },
  { id: 'children', label: 'Children' },
  { id: 'property', label: 'Property & debts' },
  { id: 'support', label: 'Support' },
  { id: 'agreement', label: 'Agreement' },
  { id: 'service', label: 'Serving papers' },
  { id: 'fee-waiver', label: 'Court fees' },
  { id: 'review', label: 'Review' },
] as const;

function californiaDefaults(answers: DivorceAnswers): DivorceAnswers {
  return {
    ...answers,
    filing_mode: 'joint',
    service: { ...answers.service, spouse_will_sign_waiver: true },
  };
}

function hasNamedChildren(answers: DivorceAnswers): boolean {
  return (answers.children ?? []).some((child) => Boolean(child.name?.trim() || child.dob));
}

export const Route = createFileRoute('/divorce/interview')({
  validateSearch: (search: Record<string, unknown>) => ({
    claimToken: typeof search.claimToken === 'string' ? search.claimToken : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
    planToken: typeof search.planToken === 'string' ? search.planToken : undefined,
    fit: search.fit === true || search.fit === 'true' ? 'true' : undefined,
    purchase: search.purchase === 'success' ? 'success' : undefined,
  }),
  beforeLoad: async ({ search }) => {
    // Paid / in-progress resumes keep working after the product is taken dark.
    if (typeof search.claimToken === 'string' && search.claimToken) {
      return;
    }
    await requireDivorceProduct();
  },
  component: DivorceInterviewPage,
});

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function DivorceInterviewPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: Route.fullPath });
  const [step, setStep] = useState(0);
  const [claimToken, setClaimToken] = useState<string | null>(search.claimToken ?? null);
  const [jurisdictionCode, setJurisdictionCode] = useState<string>(search.state?.toUpperCase() ?? '');
  const [answers, setAnswers] = useState<DivorceAnswers>({});
  const [gate, setGate] = useState<EligibilityResult | null>(null);
  const [packetAvailable, setPacketAvailable] = useState(false);
  const [paid, setPaid] = useState(false);
  const [packetReady, setPacketReady] = useState(false);
  const [accountReady, setAccountReady] = useState(false);
  const markPacketReady = useCallback(() => setPacketReady(true), []);
  const [saving, setSaving] = useState(false);
  // Draft fetched/created; the fit check must not mount with empty answers.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [needsEmailChallenge, setNeedsEmailChallenge] = useState(false);
  const [challengeEmail, setChallengeEmail] = useState('');
  const [emailAttached, setEmailAttached] = useState(false);
  const [fitCheckActive, setFitCheckActive] = useState(Boolean(search.fit));
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadGeneration = useRef(0);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const isCalifornia = jurisdictionCode === 'CA';
  const hasChildren = hasNamedChildren(answers);
  const visibleSteps = useMemo(
    () =>
      STEPS.filter((s) => {
        if (isCalifornia && s.id === 'service') return false;
        if (!hasChildren && s.id === 'agreement') return false;
        return true;
      }),
    [isCalifornia, hasChildren],
  );

  const persistSearch = useCallback(
    (token: string, keepFit = false) => {
      void navigate({
        to: '/divorce/interview',
        search: {
          claimToken: token,
          state: undefined,
          planToken: undefined,
          fit: keepFit ? 'true' : undefined,
          purchase: undefined,
        },
        replace: true,
      });
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('harbor.saved-flow', token);
      }
    },
    [navigate],
  );

  // Initial load: resume by claim token or create.
  useEffect(() => {
    const gen = ++loadGeneration.current;
    let cancelled = false;
    (async () => {
      try {
        if (search.claimToken) {
          const url = new URL(`${API_BASE_URL}/v1/divorce/draft/${search.claimToken}`);
          if (challengeEmail) url.searchParams.set('email', challengeEmail);
          const token = await getToken();
          const res = await fetch(url.toString(), {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          if (res.status === 403) {
            if (!cancelled && gen === loadGeneration.current) setNeedsEmailChallenge(true);
            return;
          }
          if (!res.ok) throw new Error('Draft not found');
          const data = (await res.json()) as DraftResponse;
          if (cancelled || gen !== loadGeneration.current) return;
          setNeedsEmailChallenge(false);
          setClaimToken(data.claim_token);
          setJurisdictionCode(data.jurisdiction_code ?? '');
          setAnswers(data.answers || {});
          setGate(data.eligibility);
          setPacketAvailable(Boolean(data.packet_available));
          setPaid(Boolean(data.paid));
          setPacketReady(Boolean(data.packet_ready));
          setEmailAttached(Boolean(data.email_attached));
          const resumeSteps =
            data.jurisdiction_code === 'CA'
              ? STEPS.filter((s) => s.id !== 'service')
              : STEPS;
          const keepFit =
            Boolean(search.fit) &&
            (!data.jurisdiction_code || isPacketState(data.jurisdiction_code)) &&
            (!data.current_step ||
              data.current_step === 'state' ||
              data.current_step === 'eligibility');
          setFitCheckActive(keepFit);
          const idx = resumeSteps.findIndex((s) => s.id === data.current_step);
          if (keepFit) {
            const eligibilityIdx = resumeSteps.findIndex((s) => s.id === 'eligibility');
            if (eligibilityIdx >= 0) setStep(eligibilityIdx);
          } else if (idx >= 0) setStep(idx);
          else if (data.current_step === 'service') {
            const feeIdx = resumeSteps.findIndex((s) => s.id === 'fee-waiver');
            if (feeIdx >= 0) setStep(feeIdx);
          }
          setLoaded(true);
          track('Divorce Interview Resumed', { state: data.jurisdiction_code ?? undefined });
          return;
        }

        const startingState = search.state?.toUpperCase() || undefined;
        const startFitCheck = Boolean(search.fit);
        // In the fit check, only packet-supported states go on the draft up
        // front; otherwise the fit check asks for the state first.
        const draftState =
          startFitCheck && !isPacketState(startingState) ? undefined : startingState;
        const res = await fetch(`${API_BASE_URL}/v1/divorce/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jurisdiction_code: draftState,
            answers: draftState === 'CA' ? californiaDefaults({}) : {},
            current_step: startFitCheck && draftState ? 'eligibility' : 'state',
            attribution: startFitCheck
              ? {
                  source: 'divorce_landing_fit_check',
                  landing_path: '/divorce',
                  state: startingState,
                }
              : {
                  source: search.state ? 'state_page' : 'direct',
                  state: startingState,
                },
          }),
        });
        if (!res.ok) throw new Error('Could not start interview');
        const data = (await res.json()) as DraftResponse;
        if (cancelled || gen !== loadGeneration.current) return;
        setClaimToken(data.claim_token);
        setJurisdictionCode(data.jurisdiction_code ?? draftState ?? '');
        setAnswers(data.answers || {});
        setGate(data.eligibility);
        setPacketAvailable(Boolean(data.packet_available));
        setPaid(Boolean(data.paid));
        setPacketReady(Boolean(data.packet_ready));
        setEmailAttached(Boolean(data.email_attached));
        setFitCheckActive(startFitCheck);
        if (startFitCheck && draftState) {
          const eligibilityIdx = (draftState === 'CA' ? STEPS.filter((s) => s.id !== 'service') : STEPS).findIndex(
            (s) => s.id === 'eligibility',
          );
          if (eligibilityIdx >= 0) setStep(eligibilityIdx);
        }
        setLoaded(true);
        persistSearch(data.claim_token, startFitCheck);
        if (search.planToken) {
          await fetch(
            `${API_BASE_URL}/v1/divorce/draft/${data.claim_token}/import-parenting-plan`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ parenting_plan_claim_token: search.planToken }),
            },
          ).catch(() => undefined);
        }
        if (!startFitCheck) {
          track('Divorce Interview Started', { state: search.state?.toUpperCase() });
        }
      } catch {
        if (!cancelled && gen === loadGeneration.current) {
          setLoadError('We could not load your interview. Please refresh or start from the divorce hub.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    search.claimToken,
    search.state,
    search.planToken,
    search.fit,
    persistSearch,
    challengeEmail,
    getToken,
  ]);

  // Stripe returns here before the webhook always lands. Poll until paid, then
  // drop ?purchase=success so a refresh does not keep polling.
  useEffect(() => {
    if (search.purchase !== 'success' || !search.claimToken) return;
    if (paid) {
      persistSearch(search.claimToken);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const url = new URL(`${API_BASE_URL}/v1/divorce/draft/${search.claimToken}`);
        if (challengeEmail) url.searchParams.set('email', challengeEmail);
        const token = await getToken();
        const res = await fetch(url.toString(), {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (res.ok) {
          const data = (await res.json()) as DraftResponse;
          if (cancelled) return;
          setPaid(Boolean(data.paid));
          setPacketReady(Boolean(data.packet_ready));
          setPacketAvailable(Boolean(data.packet_available));
          if (data.paid) {
            track('Divorce Purchase Completed', { state: data.jurisdiction_code ?? undefined });
            persistSearch(data.claim_token);
            return;
          }
        }
      } catch {
        // Keep polling; the webhook may still be in flight.
      }
      if (!cancelled && attempts < 15) {
        window.setTimeout(() => {
          void poll();
        }, 1000);
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [
    search.purchase,
    search.claimToken,
    paid,
    persistSearch,
    challengeEmail,
    getToken,
  ]);

  useEffect(() => {
    if (fitCheckActive) return;
    track('Divorce Interview Step Viewed', {
      step: visibleSteps[step]?.id,
      state: jurisdictionCode || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, fitCheckActive]);

  const flushSave = useCallback(
    async (overrides?: {
      answers?: DivorceAnswers;
      currentStep?: string;
      jurisdictionCode?: string;
    }) => {
      if (!claimToken) return null;
      const payload = overrides?.answers ?? answersRef.current;
      setSaving(true);
      try {
        const res = await fetch(`${API_BASE_URL}/v1/divorce/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            claim_token: claimToken,
            jurisdiction_code: overrides?.jurisdictionCode ?? (jurisdictionCode || undefined),
            answers: payload,
            current_step: overrides?.currentStep ?? visibleSteps[step]?.id ?? STEPS[step]?.id,
            challenge_email: challengeEmail || undefined,
          }),
        });
        if (res.ok) {
          const data = (await res.json()) as DraftResponse;
          setGate(data.eligibility);
          setPacketAvailable(Boolean(data.packet_available));
          setPaid(Boolean(data.paid));
          setPacketReady(Boolean(data.packet_ready));
          setEmailAttached(Boolean(data.email_attached));
          if (overrides?.answers) {
            setAnswers(data.answers || {});
          }
          return data;
        }
        return null;
      } finally {
        setSaving(false);
      }
    },
    [claimToken, jurisdictionCode, step, challengeEmail, visibleSteps],
  );

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void flushSave();
    }, 600);
  }, [flushSave]);

  useEffect(() => {
    if (!claimToken || fitCheckActive) return;
    scheduleSave();
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, jurisdictionCode, claimToken, scheduleSave, fitCheckActive]);

  const setSection = <K extends keyof DivorceAnswers>(key: K, value: DivorceAnswers[K]) => {
    setAnswers((a) => ({ ...a, [key]: value }));
  };

  useEffect(() => {
    if (!isCalifornia || fitCheckActive) return;
    setAnswers((a) => {
      if (a.filing_mode === 'joint' && a.service?.spouse_will_sign_waiver) return a;
      return californiaDefaults(a);
    });
  }, [isCalifornia, fitCheckActive]);

  useEffect(() => {
    if (visibleSteps[step]) return;
    setStep(Math.max(0, visibleSteps.length - 1));
  }, [visibleSteps, step]);

  const stepId = visibleSteps[step]?.id ?? STEPS[step]?.id;

  const canNext = (() => {
    if (stepId === 'state') {
      return Boolean(jurisdictionCode && answers.residency?.county?.trim());
    }
    if (stepId === 'parties')
      return Boolean(answers.petitioner?.name?.trim() && answers.respondent?.name?.trim());
    return true;
  })();

  const isLastStep = step === visibleSteps.length - 1;

  const goNext = async () => {
    if (!canNext) return;
    track('Divorce Interview Step Completed', {
      step: stepId,
      state: jurisdictionCode || undefined,
    });
    await flushSave();
    if (!isLastStep) setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  /* ------------------------- gate bounce-outs (blocked / wait) ------------ */

  const showGateScreen = !fitCheckActive && gate && gate.status !== 'eligible' && step >= 1;

  if (loadError) {
    return (
      <div className='min-h-screen bg-white px-6 py-24 text-center'>
        <p className='text-gray-700' style={nunito}>{loadError}</p>
        <Link to='/divorce' className='mt-6 inline-block font-semibold text-primary'>
          Back to divorce hub
        </Link>
      </div>
    );
  }

  if (needsEmailChallenge) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white px-6'>
        <div className='w-full max-w-md rounded-2xl border border-gray-200 p-8 shadow-sm'>
          <h1 className='text-xl font-bold text-gray-900' style={nunito}>
            Confirm your email
          </h1>
          <p className='mt-2 text-sm text-gray-600' style={nunito}>
            This saved interview is protected. Enter the email address on the draft to continue.
          </p>
          <div className='mt-4'>
            <Input
              type='email'
              placeholder='you@example.com'
              value={challengeEmail}
              onChange={(e) => setChallengeEmail(e.target.value)}
            />
          </div>
          <p className='mt-3 text-xs text-gray-500' style={nunito}>
            The page reloads your draft automatically once the email matches.
          </p>
        </div>
      </div>
    );
  }

  if (!claimToken || !loaded) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white'>
        <p className='text-gray-600' style={nunito}>Starting your interview…</p>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title='Divorce interview | Harbor'
        description='Answer plain-language questions; Harbor prepares your official state divorce forms.'
        path='/divorce/interview'
        noindex
      />
      <div className='flex flex-col bg-gray-50 max-md:min-h-[100dvh]'>
      <header className='border-b border-gray-200 bg-white'>
        <div className='mx-auto flex max-w-6xl items-center justify-between px-4 py-4'>
          <Link
            to='/divorce'
            className='inline-flex items-center gap-2 text-sm font-medium text-primary'
            style={nunito}
          >
            <FiArrowLeft />
            <span className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary'>
              <img src={shipSvg} alt='' className='h-3.5 w-3.5' />
            </span>
            Harbor Divorce
          </Link>
          <span className='text-xs text-gray-500'>{saving ? 'Saving…' : 'Saved'}</span>
        </div>
      </header>

      <div className='max-md:flex max-md:flex-1 max-md:flex-col max-md:justify-center'>
      {fitCheckActive ? (
        <DivorceFitCheck
          answers={answers}
          gate={gate}
          saving={saving}
          emailAttached={emailAttached}
          jurisdictionCode={jurisdictionCode}
          initialStateCode={search.state?.toUpperCase() || undefined}
          onSave={async (next) => {
            const data = await flushSave({ answers: next, currentStep: 'eligibility' });
            if (!data) return null;
            return {
              answers: data.answers,
              eligibility: data.eligibility,
              email_attached: Boolean(data.email_attached),
            };
          }}
          onSelectState={async (code) => {
            const upper = code.toUpperCase();
            setJurisdictionCode(upper);
            const nextAnswers =
              upper === 'CA' ? californiaDefaults(answersRef.current) : answersRef.current;
            const data = await flushSave({
              answers: nextAnswers,
              currentStep: isPacketState(upper) ? 'eligibility' : 'state',
              jurisdictionCode: upper,
            });
            if (!data) return null;
            return {
              answers: data.answers,
              eligibility: data.eligibility,
              email_attached: Boolean(data.email_attached),
            };
          }}
          onContinue={() => {
            const stateIdx = visibleSteps.findIndex((s) => s.id === 'state');
            setFitCheckActive(false);
            if (stateIdx >= 0) setStep(stateIdx);
            if (claimToken) persistSearch(claimToken, false);
            void flushSave({ currentStep: 'state' });
            track('Divorce Interview Started', { state: jurisdictionCode || undefined });
          }}
          onAttachEmail={async (email) => {
            if (!claimToken) return false;
            try {
              const res = await fetch(`${API_BASE_URL}/v1/divorce/draft`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  claim_token: claimToken,
                  email,
                  email_private: true,
                  challenge_email: challengeEmail || undefined,
                }),
              });
              if (!res.ok) return false;
              setEmailAttached(true);
              setChallengeEmail(email.trim().toLowerCase());
              const resumeResponse = await fetch(
                `${API_BASE_URL}/v1/divorce/draft/${claimToken}/send-resume-link`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: email.trim().toLowerCase() }),
                },
              );
              if (!resumeResponse.ok) return false;
              track('Divorce Resume Link Sent');
              return true;
            } catch {
              return false;
            }
          }}
        />
      ) : showGateScreen ? (
        <GateScreen gate={gate} onBack={() => setStep(1)} />
      ) : (
        <WizardShell
          steps={[...visibleSteps]}
          activeIndex={step}
          onBack={goBack}
          onNext={goNext}
          onStepSelect={(index) => {
            const countyFilled = Boolean(answers.residency?.county?.trim());
            if (stepId === 'state' && !countyFilled) return;
            if (index < step) {
              setStep(index);
              return;
            }
            if (stepId === 'state' && countyFilled) setStep(index);
          }}
          canGoBack={step > 0}
          canGoNext={canNext}
          nextLabel='Continue'
          showNext={!isLastStep}
        >
          {stepId === 'state' && (
            <StateStep
              jurisdictionCode={jurisdictionCode}
              setJurisdictionCode={setJurisdictionCode}
              answers={answers}
              setSection={setSection}
            />
          )}
          {stepId === 'eligibility' && (
            <EligibilityStep answers={answers} setSection={setSection} />
          )}
          {stepId === 'parties' && (
            <PartiesStep
              answers={answers}
              setSection={setSection}
              isCalifornia={isCalifornia}
            />
          )}
          {stepId === 'marriage' && <MarriageStep answers={answers} setSection={setSection} />}
          {stepId === 'children' && <ChildrenStep answers={answers} setSection={setSection} />}
          {stepId === 'property' && <PropertyStep answers={answers} setSection={setSection} />}
          {stepId === 'support' && <SupportStep answers={answers} setSection={setSection} />}
          {stepId === 'agreement' && (
            <AgreementStep
              answers={answers}
              setSection={setSection}
              claimToken={claimToken}
              challengeEmail={challengeEmail}
              onImported={(a) => setAnswers(a)}
            />
          )}
          {stepId === 'service' && <ServiceStep answers={answers} setSection={setSection} />}
          {stepId === 'fee-waiver' && (
            <FeeWaiverStep
              answers={answers}
              setSection={setSection}
              isCalifornia={isCalifornia}
            />
          )}
          {stepId === 'review' && (
            <ReviewStep
              answers={answers}
              jurisdictionCode={jurisdictionCode}
              gate={gate}
              packetAvailable={packetAvailable}
              paid={paid}
              packetReady={packetReady}
              accountReady={accountReady}
              claimToken={claimToken}
              challengeEmail={challengeEmail}
              onPacketReady={markPacketReady}
              onAccountReady={() => setAccountReady(true)}
              onBeforeCheckout={async () => {
                await flushSave();
              }}
            />
          )}
        </WizardShell>
      )}
      </div>
      </div>

      <Footer />
    </div>
  );
}

/* --------------------------------------------------------------- gate UI */

function GateScreen({ gate, onBack }: { gate: EligibilityResult; onBack: () => void }) {
  const isWait = gate.status === 'wait';
  return (
    <div className='mx-auto max-w-3xl px-4 py-12'>
      <div className='rounded-2xl border border-gray-200 bg-white p-8 shadow-sm'>
        <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
          {isWait ? 'Not quite yet, but your answers are saved' : 'This path isn’t the right fit'}
        </h2>
        <div className='mt-4 space-y-4'>
          {gate.reasons.map((r) => (
            <div key={r.code} className='rounded-xl bg-gray-50 p-4'>
              <p className='text-gray-800' style={nunito}>
                {r.message}
              </p>
              {r.resource_url ? (
                <a
                  href={r.resource_url}
                  className='mt-2 inline-block text-sm font-semibold text-primary'
                  target={r.resource_url.startsWith('http') ? '_blank' : undefined}
                  rel='noreferrer'
                >
                  {r.code === 'dv_safety' ? 'National Domestic Violence Hotline' : 'Find a professional'}
                </a>
              ) : null}
            </div>
          ))}
        </div>
        <div className='mt-8 flex flex-wrap gap-3'>
          <Link
            to='/divorce'
            className='rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white'
          >
            See your state’s free checklist
          </Link>
          <button
            type='button'
            onClick={onBack}
            className='rounded-2xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-800'
          >
            Review my answers
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ step pieces */

function YesNo({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <div className='rounded-xl border border-gray-200 bg-white p-4'>
      <p className='font-medium text-gray-900' style={nunito}>
        {label}
      </p>
      {hint ? (
        <p className='mt-1 text-sm text-gray-500' style={nunito}>
          {hint}
        </p>
      ) : null}
      <div className='mt-3 flex gap-2'>
        {[
          { v: true, label: 'Yes' },
          { v: false, label: 'No' },
        ].map((opt) => (
          <button
            key={String(opt.v)}
            type='button'
            onClick={() => onChange(opt.v)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
              value === opt.v
                ? 'bg-primary text-white'
                : 'border border-gray-300 bg-white text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

type SectionSetter = <K extends keyof DivorceAnswers>(key: K, value: DivorceAnswers[K]) => void;

function StateStep({
  jurisdictionCode,
  setJurisdictionCode,
  answers,
  setSection,
}: {
  jurisdictionCode: string;
  setJurisdictionCode: (code: string) => void;
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const residency = answers.residency ?? {};
  const getCountySuggestions = useCallback(
    (query: string, signal: AbortSignal) =>
      suggestCounties(query, jurisdictionCode, signal),
    [jurisdictionCode],
  );
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Where will you file?
      </h2>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>State</label>
        <select
          className='w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-3'
          value={jurisdictionCode}
          onChange={(e) => setJurisdictionCode(e.target.value)}
        >
          <option value=''>Select your state…</option>
          {DIVORCE_JURISDICTIONS.map((j) => (
            <option key={j.code} value={j.code}>
              {j.name}
            </option>
          ))}
        </select>
      </div>
      <div className='grid gap-4 sm:grid-cols-2'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>
            Months you’ve lived in this state
          </label>
          <Input
            type='number'
            min={0}
            value={residency.months_in_state ?? ''}
            onChange={(e) =>
              setSection('residency', {
                ...residency,
                months_in_state: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </div>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>
            Months you’ve lived in this county
          </label>
          <Input
            type='number'
            min={0}
            value={residency.months_in_county ?? ''}
            onChange={(e) =>
              setSection('residency', {
                ...residency,
                months_in_county: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </div>
        <div className='sm:col-span-2'>
          <SuggestInput
            label='County you will file in (required)'
            value={residency.county ?? ''}
            onChange={(value) =>
              setSection('residency', { ...residency, county: value })
            }
            onPick={(suggestion) => {
              setSection('residency', {
                ...residency,
                county: suggestion.city,
              });
              track('Divorce Filing County Suggested', {
                state: jurisdictionCode || undefined,
              });
            }}
            getSuggestions={getCountySuggestions}
            minChars={0}
            autoComplete='address-level2'
            placeholder={jurisdictionCode === 'CA' ? 'Start typing a county' : undefined}
          />
        </div>
      </div>
      <YesNo
        label='Are you in the state because of a military assignment?'
        hint='Being stationed somewhere isn’t always the same as being a resident.'
        value={residency.military}
        onChange={(v) => setSection('residency', { ...residency, military: v })}
      />
    </div>
  );
}

function EligibilityStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const e = answers.eligibility ?? {};
  const service = answers.service ?? {};
  const set = (key: string, v: boolean) => setSection('eligibility', { ...e, [key]: v });
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        A few questions first
      </h2>
      <p className='text-gray-600' style={nunito}>
        These make sure a do-it-yourself divorce is actually the right tool for your situation.
        Some answers point you to better help instead.
      </p>
      <YesNo
        label='Do you want a divorce (not an annulment)?'
        value={e.wants_annulment === undefined ? undefined : !e.wants_annulment}
        onChange={(v) => set('wants_annulment', !v)}
      />
      <YesNo
        label={DV_SCREEN_QUESTION}
        hint={DV_SCREEN_HINT}
        value={e.dv_flag === undefined ? undefined : !e.dv_flag}
        onChange={(v) => set('dv_flag', !v)}
      />
      <YesNo
        label='Do you and your spouse agree on all major terms?'
        hint='Property, debts, support, and (if you have kids) the parenting schedule.'
        value={e.contested === undefined ? undefined : !e.contested}
        onChange={(v) => set('contested', !v)}
      />
      <YesNo
        label='Is your marriage a covenant marriage?'
        hint='A special marriage type in Arizona, Arkansas, and Louisiana. Most marriages are not.'
        value={e.covenant_marriage}
        onChange={(v) => set('covenant_marriage', v)}
      />
      <YesNo
        label='Is either of you in an active bankruptcy case?'
        value={e.active_bankruptcy}
        onChange={(v) => set('active_bankruptcy', v)}
      />
      <YesNo
        label='Any open family-law cases between you (custody, protective orders, an earlier divorce)?'
        value={e.pending_cases}
        onChange={(v) => set('pending_cases', v)}
      />
      <YesNo
        label='Was either of you married before, and if so, is that divorce final?'
        hint='Answer “No” only if a previous divorce was never finalized.'
        value={e.prior_marriage_not_final === undefined ? undefined : !e.prior_marriage_not_final}
        onChange={(v) => set('prior_marriage_not_final', !v)}
      />
      <YesNo
        label='Is your spouse on active military duty?'
        value={e.spouse_military}
        onChange={(v) => set('spouse_military', v)}
      />
      {e.spouse_military ? (
        <YesNo
          label='Will your spouse sign a waiver or acceptance of service?'
          value={service.spouse_will_sign_waiver}
          onChange={(v) => setSection('service', { ...service, spouse_will_sign_waiver: v })}
        />
      ) : null}
      <YesNo
        label='Is anyone currently pregnant?'
        value={e.pregnant}
        onChange={(v) => set('pregnant', v)}
      />
      <YesNo
        label='Is child support already handled by a state agency, or does either parent receive public benefits (TANF, Medicaid)?'
        value={e.iv_d_case}
        onChange={(v) => set('iv_d_case', v)}
      />
    </div>
  );
}

function PartyFields({
  title,
  party,
  onChange,
}: {
  title: string;
  party: Party;
  onChange: (p: Party) => void;
}) {
  return (
    <div className='rounded-xl border border-gray-200 bg-white p-4'>
      <h3 className='mb-3 font-semibold text-gray-900' style={nunito}>
        {title}
      </h3>
      <div className='grid gap-3 sm:grid-cols-2'>
        <Input
          label='Full legal name'
          value={party.name ?? ''}
          onChange={(e) => onChange({ ...party, name: e.target.value })}
        />
        <Input
          label='Phone'
          value={party.phone ?? ''}
          onChange={(e) => onChange({ ...party, phone: e.target.value })}
        />
        <Input
          label='Street address'
          value={party.address ?? ''}
          onChange={(e) => onChange({ ...party, address: e.target.value })}
        />
        <Input
          label='City'
          value={party.city ?? ''}
          onChange={(e) => onChange({ ...party, city: e.target.value })}
        />
        <Input
          label='State'
          value={party.state ?? ''}
          onChange={(e) => onChange({ ...party, state: e.target.value })}
        />
        <Input
          label='ZIP'
          value={party.zip ?? ''}
          onChange={(e) => onChange({ ...party, zip: e.target.value })}
        />
        <Input
          label='Email'
          type='email'
          value={party.email ?? ''}
          onChange={(e) => onChange({ ...party, email: e.target.value })}
        />
      </div>
    </div>
  );
}

function PartiesStep({
  answers,
  setSection,
  isCalifornia,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
  isCalifornia: boolean;
}) {
  const joint = isCalifornia || answers.filing_mode === 'joint';
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Who is filing?
      </h2>
      {isCalifornia ? (
        <p className='text-gray-600' style={nunito}>
          California’s joint petition lists you as Petitioner 1 and Petitioner 2; there is no
          legal advantage to either slot, but the same person must stay 1 or 2 on every form. Both
          of you sign FL-700 and FL-710; no one has to be served.
        </p>
      ) : (
        <p className='text-gray-600' style={nunito}>
          Court forms use “Petitioner” (the person filing) and “Respondent” (the other spouse),
          never husband or wife. In some states you can file together as co-petitioners.
        </p>
      )}
      {isCalifornia ? null : (
        <div className='flex gap-2'>
          {[
            { v: 'sole' as const, label: 'I’m filing' },
            { v: 'joint' as const, label: 'We’re filing together' },
          ].map((opt) => (
            <button
              key={opt.v}
              type='button'
              onClick={() => setSection('filing_mode', opt.v)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                (answers.filing_mode ?? 'sole') === opt.v
                  ? 'bg-primary text-white'
                  : 'border border-gray-300 bg-white text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      <PartyFields
        title={isCalifornia ? 'You (Petitioner 1)' : joint ? 'You (co-petitioner 1)' : 'You (petitioner)'}
        party={answers.petitioner ?? {}}
        onChange={(p) => setSection('petitioner', p)}
      />
      <PartyFields
        title={
          isCalifornia
            ? 'Your spouse (Petitioner 2)'
            : joint
              ? 'Your spouse (co-petitioner 2)'
              : 'Your spouse (respondent)'
        }
        party={answers.respondent ?? {}}
        onChange={(p) => setSection('respondent', p)}
      />
      <YesNo
        label='Does either of you want a former name restored as part of the divorce?'
        value={answers.name_change_requested}
        onChange={(v) => setSection('name_change_requested', v)}
      />
    </div>
  );
}

function MarriageStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const m = answers.marriage ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Your marriage
      </h2>
      <div className='grid gap-4 sm:grid-cols-2'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>Date of marriage</label>
          <Input
            type='date'
            value={m.date ?? ''}
            onChange={(e) => setSection('marriage', { ...m, date: e.target.value })}
          />
        </div>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>
            Date of separation (if separated)
          </label>
          <Input
            type='date'
            value={m.separation_date ?? ''}
            onChange={(e) => setSection('marriage', { ...m, separation_date: e.target.value })}
          />
        </div>
        <SuggestInput
          label='City married in'
          value={m.city ?? ''}
          autoComplete='address-level2'
          minChars={2}
          getSuggestions={suggestCities}
          onChange={(city) => setSection('marriage', { ...m, city })}
          onPick={(place) => {
            track('Divorce Marriage Place Suggested', { field: 'city' });
            setSection('marriage', {
              ...m,
              city: place.city,
              state: place.region || m.state,
            });
          }}
        />
        <SuggestInput
          label='State/country married in'
          value={m.state ?? ''}
          autoComplete='address-level1'
          getSuggestions={suggestStateCountry}
          onChange={(state) => setSection('marriage', { ...m, state })}
          onPick={(place) => {
            track('Divorce Marriage Place Suggested', { field: 'state' });
            setSection('marriage', { ...m, state: place.region });
          }}
        />
      </div>
      <YesNo
        label='Is this a common-law (informal) marriage?'
        hint='Recognized in a few states, like Texas and Colorado.'
        value={m.common_law}
        onChange={(v) => setSection('marriage', { ...m, common_law: v })}
      />
    </div>
  );
}

function ChildrenStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const children = answers.children ?? [];
  const update = (idx: number, patch: Partial<Child>) => {
    const next = [...children];
    next[idx] = { ...next[idx], ...patch };
    setSection('children', next);
  };
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Children of the marriage
      </h2>
      <p className='text-gray-600' style={nunito}>
        Skip this step if you don’t have children together. Include all children under 18 (adult
        children can still matter for support in some states; include them and the court will
        sort it out).
      </p>
      {children.map((c, idx) => (
        <div key={idx} className='rounded-xl border border-gray-200 bg-white p-4'>
          <div className='grid gap-3 sm:grid-cols-3'>
            <Input
              label='Name'
              value={c.name ?? ''}
              onChange={(e) => update(idx, { name: e.target.value })}
            />
            <div>
              <label className='mb-1 block text-sm text-gray-600'>Date of birth</label>
              <Input
                type='date'
                value={c.dob ?? ''}
                onChange={(e) => update(idx, { dob: e.target.value })}
              />
            </div>
            <div>
              <label className='mb-1 block text-sm text-gray-600'>Lives mostly with</label>
              <select
                className='w-full rounded-xl border border-gray-200 bg-white px-4 py-3'
                value={c.lives_with ?? ''}
                onChange={(e) =>
                  update(idx, { lives_with: (e.target.value || undefined) as Child['lives_with'] })
                }
              >
                <option value=''>Select…</option>
                <option value='petitioner'>Me</option>
                <option value='respondent'>My spouse</option>
                <option value='both'>Both of us equally</option>
                <option value='other'>Someone else</option>
              </select>
            </div>
          </div>
          <button
            type='button'
            className='mt-3 text-sm font-semibold text-red-600'
            onClick={() => setSection('children', children.filter((_, i) => i !== idx))}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type='button'
        className='text-sm font-semibold text-primary'
        onClick={() => setSection('children', [...children, {}])}
      >
        + Add a child
      </button>
    </div>
  );
}

function PropertyStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const p = answers.property ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Property & debts
      </h2>
      <YesNo
        label='Do you own real estate together?'
        value={p.has_real_estate}
        onChange={(v) => setSection('property', { ...p, has_real_estate: v })}
      />
      <YesNo
        label='Does either of you have retirement accounts to divide?'
        hint='Splitting retirement usually needs a separate court order (QDRO). We’ll flag this for attorney review.'
        value={p.has_retirement}
        onChange={(v) => setSection('property', { ...p, has_retirement: v })}
      />
      <YesNo
        label='Does either of you own a business?'
        value={p.has_business}
        onChange={(v) => setSection('property', { ...p, has_business: v })}
      />
    </div>
  );
}

function SupportStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const s = answers.support ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Support
      </h2>
      {hasNamedChildren(answers) ? (
        <YesNo
          label='Will one of you pay child support?'
          value={s.child_support_requested}
          onChange={(v) => setSection('support', { ...s, child_support_requested: v })}
        />
      ) : null}
      <YesNo
        label='Will one of you pay spousal support (alimony)?'
        value={s.spousal_support_requested}
        onChange={(v) => setSection('support', { ...s, spousal_support_requested: v })}
      />
      {hasNamedChildren(answers) ? (
        <p className='text-sm text-gray-500' style={nunito}>
          Need a number? Our free{' '}
          <Link to='/child-support-calculator' className='font-semibold text-primary'>
            child support calculator
          </Link>{' '}
          uses your state’s guidelines.
        </p>
      ) : null}
    </div>
  );
}

function AgreementStep({
  answers,
  setSection,
  claimToken,
  challengeEmail,
  onImported,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
  claimToken: string;
  challengeEmail: string;
  onImported: (a: DivorceAnswers) => void;
}) {
  const a = answers.agreement ?? {};
  const [planToken, setPlanToken] = useState('');
  const [importState, setImportState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  const importPlan = async () => {
    if (!planToken.trim()) return;
    setImportState('loading');
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/divorce/draft/${claimToken}/import-parenting-plan`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parenting_plan_claim_token: planToken.trim(),
            challenge_email: challengeEmail || undefined,
          }),
        },
      );
      if (!res.ok) throw new Error('import failed');
      const data = (await res.json()) as DraftResponse;
      onImported(data.answers);
      setImportState('done');
      track('Divorce Interview Parenting Plan Imported');
    } catch {
      setImportState('error');
    }
  };

  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Your agreement
      </h2>
      <div className='rounded-xl border border-primary/30 bg-primary/5 p-4'>
        <p className='font-medium text-gray-900' style={nunito}>
          Already built a parenting plan with Harbor?
        </p>
        <p className='mt-1 text-sm text-gray-600' style={nunito}>
          Paste the link or code from your parenting plan email and we’ll import your schedule.
        </p>
        <div className='mt-3 flex flex-wrap gap-2'>
          <div className='min-w-[240px] flex-1'>
            <Input
              placeholder='Parenting plan code'
              value={planToken}
              onChange={(e) => setPlanToken(e.target.value)}
            />
          </div>
          <button
            type='button'
            onClick={importPlan}
            disabled={importState === 'loading'}
            className='rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white disabled:opacity-50'
          >
            {importState === 'loading' ? 'Importing…' : 'Import'}
          </button>
        </div>
        {importState === 'done' ? (
          <p className='mt-2 text-sm font-medium text-green-700'>Imported.</p>
        ) : null}
        {importState === 'error' ? (
          <p className='mt-2 text-sm font-medium text-red-600'>
            We couldn’t find that plan. Check the code and try again.
          </p>
        ) : null}
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>
          Custody schedule (if you have children)
        </label>
        <Input
          placeholder='e.g. 2-2-3, week-on-week-off'
          value={a.schedule_slug ?? ''}
          onChange={(e) => setSection('agreement', { ...a, schedule_slug: e.target.value })}
        />
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Holidays</label>
        <Input
          placeholder='e.g. alternate major holidays each year'
          value={a.holidays ?? ''}
          onChange={(e) => setSection('agreement', { ...a, holidays: e.target.value })}
        />
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Major decisions</label>
        <select
          className='w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-3'
          value={a.decision_making ?? ''}
          onChange={(e) =>
            setSection('agreement', {
              ...a,
              decision_making: (e.target.value || undefined) as
                | 'joint'
                | 'petitioner'
                | 'respondent'
                | undefined,
            })
          }
        >
          <option value=''>Select…</option>
          <option value='joint'>We decide together</option>
          <option value='petitioner'>I decide</option>
          <option value='respondent'>My spouse decides</option>
        </select>
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Expense split</label>
        <Input
          placeholder='e.g. 50/50 after receipts'
          value={a.expense_split ?? ''}
          onChange={(e) => setSection('agreement', { ...a, expense_split: e.target.value })}
        />
      </div>
    </div>
  );
}

function ServiceStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const s = answers.service ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Serving the papers
      </h2>
      <p className='text-gray-600' style={nunito}>
        After filing, your spouse has to be formally notified. In amicable cases they usually just
        sign a waiver; no process server at the door.
      </p>
      <YesNo
        label='Will your spouse sign a waiver or acceptance of service?'
        value={s.spouse_will_sign_waiver}
        onChange={(v) => setSection('service', { ...s, spouse_will_sign_waiver: v })}
      />
      <YesNo
        label='Do you know your spouse’s current address?'
        value={s.spouse_address_known}
        onChange={(v) => setSection('service', { ...s, spouse_address_known: v })}
      />
      <YesNo
        label='Do you still live at the same address?'
        hint='Service rules differ for separated-but-cohabiting couples.'
        value={s.same_household}
        onChange={(v) => setSection('service', { ...s, same_household: v })}
      />
    </div>
  );
}

function FeeWaiverStep({
  answers,
  setSection,
  isCalifornia,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
  isCalifornia: boolean;
}) {
  const f = answers.fee_waiver ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Court filing fees
      </h2>
      <p className='text-gray-600' style={nunito}>
        {isCalifornia
          ? 'A California joint petition is two first-paper fees (about $870 total). If you cannot afford it, each of you may need your own fee waiver (FW-001); one waiver does not cover both of you.'
          : 'Courts charge a filing fee, but every state lets low-income filers ask for a waiver.'}
      </p>
      <YesNo
        label='Do you want to apply for a fee waiver?'
        value={f.requested}
        onChange={(v) => setSection('fee_waiver', { ...f, requested: v })}
      />
      {f.requested ? (
        <div className='grid gap-4 sm:grid-cols-2'>
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>Household size</label>
            <Input
              type='number'
              min={1}
              value={f.household_size ?? ''}
              onChange={(e) =>
                setSection('fee_waiver', {
                  ...f,
                  household_size: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              Monthly household income ($)
            </label>
            <Input
              type='number'
              min={0}
              value={f.monthly_income_cents != null ? f.monthly_income_cents / 100 : ''}
              onChange={(e) =>
                setSection('fee_waiver', {
                  ...f,
                  monthly_income_cents:
                    e.target.value === '' ? undefined : Math.round(Number(e.target.value) * 100),
                })
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

async function packetErrorMessage(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as
    | {
        detail?:
          | string
          | {
              code?: string;
              message?: string;
              issues?: { message?: string; path?: string }[];
            };
      }
    | null;
  const detail = body?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    if (detail.code === 'incomplete') {
      const labels: Record<string, string> = {
        'residency.county': 'Add the county you will file in.',
        'petitioner.name': 'Add your name.',
        'respondent.name': 'Add your spouse’s name.',
        'marriage.date': 'Add the date you married.',
      };
      const issues = (detail.issues ?? [])
        .map((i) => (i.path && labels[i.path]) || i.message)
        .filter(Boolean);
      if (issues.length) {
        return issues.join(' ');
      }
      return 'A few answers still need to be filled in before we can prepare the forms.';
    }
    if (detail.code === 'ineligible') {
      return 'This case is no longer eligible for form prep.';
    }
    if (detail.message) return detail.message;
  }
  return 'We could not prepare the forms right now. Your purchase is saved; try again shortly.';
}

function ReviewStep({
  answers,
  jurisdictionCode,
  gate,
  packetAvailable,
  paid,
  packetReady,
  accountReady,
  claimToken,
  challengeEmail,
  onPacketReady,
  onAccountReady,
  onBeforeCheckout,
}: {
  answers: DivorceAnswers;
  jurisdictionCode: string;
  gate: EligibilityResult | null;
  packetAvailable: boolean;
  paid: boolean;
  packetReady: boolean;
  accountReady: boolean;
  claimToken: string;
  challengeEmail: string;
  onPacketReady: () => void;
  onAccountReady: () => void;
  onBeforeCheckout: () => Promise<void>;
}) {
  const { getToken } = useAuth();
  const jurisdiction = DIVORCE_JURISDICTIONS.find((j) => j.code === jurisdictionCode);
  const isCalifornia = jurisdictionCode === 'CA';
  const reviewWarnings = (gate?.warnings ?? []).filter((w) => w.code !== 'business_valuation');
  const [checkoutState, setCheckoutState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [packetState, setPacketState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    packetReady ? 'ready' : 'idle',
  );
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [formTitles, setFormTitles] = useState<string[]>([]);
  const [packetError, setPacketError] = useState<string | null>(null);
  const prepareStarted = useRef(false);
  const savedCounty = answers.residency?.county?.trim() ?? '';

  const startCheckout = async () => {
    if (!savedCounty || !accountReady) {
      setCheckoutState('error');
      return;
    }
    setCheckoutState('loading');
    setCheckoutError(null);
    try {
      track('Divorce Packet Checkout Started', { state: jurisdictionCode || undefined });
      await onBeforeCheckout();
      const res = await apiFetch(getToken, '/v1/subscription/create-divorce-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_token: claimToken,
          challenge_email: challengeEmail || undefined,
        }),
      });
      if (!res.ok) throw new Error(await packetErrorMessage(res));
      const data = (await res.json()) as { checkout_url: string };
      window.location.href = data.checkout_url;
    } catch (error) {
      setCheckoutState('error');
      setCheckoutError(
        error instanceof Error
          ? error.message
          : 'Checkout isn’t available right now. Your answers are saved; try again shortly.',
      );
    }
  };

  const preparePacket = useCallback(
    async (preferExisting: boolean) => {
      setPacketState('loading');
      setPacketError(null);
      const body = {
        claim_token: claimToken,
        challenge_email: challengeEmail || undefined,
      };
      try {
        if (preferExisting) {
          const existing = await apiFetch(getToken, '/v1/divorce/packet/link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (existing.ok) {
            const data = (await existing.json()) as { download_url: string; form_titles?: string[] };
            setDownloadUrl(data.download_url);
            setFormTitles(data.form_titles ?? []);
            setPacketState('ready');
            onPacketReady();
            return;
          }
        }
        const res = await apiFetch(getToken, '/v1/divorce/packet', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(await packetErrorMessage(res));
        const data = (await res.json()) as {
          download_url: string;
          form_titles?: string[];
        };
        setDownloadUrl(data.download_url);
        setFormTitles(data.form_titles ?? []);
        setPacketState('ready');
        onPacketReady();
        track('Divorce Packet Generated', { state: jurisdictionCode || undefined });
      } catch (err) {
        setPacketState('error');
        setPacketError(err instanceof Error ? err.message : 'We could not prepare the forms right now.');
      }
    },
    [challengeEmail, claimToken, getToken, jurisdictionCode, onPacketReady],
  );

  useEffect(() => {
    if (!paid || !accountReady || prepareStarted.current || !savedCounty) return;
    prepareStarted.current = true;
    void preparePacket(packetReady);
  }, [accountReady, paid, packetReady, preparePacket, savedCounty]);

  const downloadPacket = () => {
    if (!accountReady) {
      setPacketError('Verify your email to save this purchase before downloading.');
      return;
    }
    if (!savedCounty) {
      setPacketError('Go back to Your state and add the county you will file in.');
      return;
    }
    if (!downloadUrl) {
      void preparePacket(packetReady);
      return;
    }
    track('Divorce Packet Downloaded', { state: jurisdictionCode || undefined });
    window.location.assign(downloadUrl);
  };
  const rows: [string, string | undefined][] = [
    ['State', jurisdiction?.name],
    [
      'Filing',
      isCalifornia || answers.filing_mode === 'joint'
        ? 'Joint petition (both sign)'
        : 'One spouse filing',
    ],
    [isCalifornia ? 'Petitioner 1' : 'You', answers.petitioner?.name],
    [isCalifornia ? 'Petitioner 2' : 'Your spouse', answers.respondent?.name],
    ['Married', answers.marriage?.date],
    ['County', answers.residency?.county],
    ['Children', answers.children?.length ? String(answers.children.length) : 'None'],
    ['Custody schedule', answers.agreement?.schedule_slug],
    ['Fee waiver', answers.fee_waiver?.requested ? 'Requested' : 'Not requested'],
  ];
  return (
    <div className='space-y-6'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Review
      </h2>
      <div className='overflow-hidden rounded-xl border border-gray-200 bg-white'>
        {rows.map(([label, value]) => (
          <div key={label} className='flex justify-between border-b border-gray-100 px-4 py-3 last:border-0'>
            <span className='text-sm text-gray-500' style={nunito}>
              {label}
            </span>
            <span className='text-sm font-medium text-gray-900' style={nunito}>
              {value || '-'}
            </span>
          </div>
        ))}
      </div>
      {reviewWarnings.length ? (
        <div className='space-y-3'>
          {reviewWarnings.map((w) => (
            <div key={w.code} className='rounded-xl bg-amber-50 p-4 text-sm text-amber-900' style={nunito}>
              {w.message}{' '}
              {w.resource_url ? (
                <a href={w.resource_url} className='font-semibold underline'>
                  Find a professional
                </a>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {paid ? (
        <div className='rounded-xl border border-gray-200 bg-white p-6 sm:p-8'>
          <span className='mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary'>
            <img src={shipSvg} alt='' className='h-5 w-5' />
          </span>
          <h3 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            {!accountReady
              ? 'Payment confirmed.'
              : packetState === 'ready'
                ? 'Your forms are ready.'
                : 'Preparing your forms'}
          </h3>
          <p className='mt-2 text-base leading-relaxed text-gray-600' style={nunito}>
            {!accountReady
              ? 'Save this purchase to your Harbor account, then we’ll prepare your forms.'
              : 'Download the filled packet. Review every page, sign where the forms say to sign, then file with the court. Harbor does not file, serve, or appear for you.'}
          </p>
          <DivorceAccountGate
            claimToken={claimToken}
            ready={accountReady}
            onReady={onAccountReady}
            purchased
          />
          {accountReady && formTitles.length ? (
            <ul className='mt-6 space-y-2' style={nunito}>
              {formTitles.map((title) => (
                <li key={title} className='flex items-start gap-3 text-gray-800'>
                  <FiCheck className='mt-0.5 h-5 w-5 shrink-0 text-primary' />
                  {title}
                </li>
              ))}
            </ul>
          ) : null}
          {accountReady ? (
            <>
              <button
                type='button'
                onClick={downloadPacket}
                disabled={packetState === 'loading'}
                className='mt-8 w-full rounded-2xl bg-primary px-8 py-4 text-lg font-semibold text-white disabled:opacity-50'
              >
                {packetState === 'loading'
                  ? 'Preparing your forms…'
                  : packetState === 'ready'
                    ? 'Download my forms'
                    : 'Try again'}
              </button>
              {packetError || !savedCounty ? (
                <p className='mt-3 text-center text-sm font-medium text-red-600' style={nunito}>
                  {packetError || 'Go back to Your state and add the county you will file in.'}
                </p>
              ) : null}
              <DivorcePhoneActivation />
            </>
          ) : null}
        </div>
      ) : packetAvailable ? (
        <div className='rounded-xl border border-gray-200 bg-white p-6 sm:p-8'>
          <span className='mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary'>
            <img src={shipSvg} alt='' className='h-5 w-5' />
          </span>
          <h3 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            You already did the hard part.
          </h3>
          <p className='mt-2 text-base leading-relaxed text-gray-600' style={nunito}>
            Harbor fills the official forms from these answers. You review every
            page, then you file.
          </p>
          <ul className='mt-6 space-y-3' style={nunito}>
            {(isCalifornia
              ? [
                  'Both of you sign the same papers',
                  'Nobody has to be served',
                  'If the court updates a form, Harbor regenerates yours',
                  'A year of Harbor for the schedule and the messages',
                ]
              : [
                  'Official forms, filled from your answers',
                  'You review every page before you file',
                  'If the court updates a form, Harbor regenerates yours',
                  'A year of Harbor for the schedule and the messages',
                ]
            ).map((item) => (
              <li key={item} className='flex items-start gap-3 text-gray-800'>
                <FiCheck className='mt-0.5 h-5 w-5 shrink-0 text-primary' />
                {item}
              </li>
            ))}
          </ul>
          <button
            type='button'
            onClick={() => {
              void startCheckout();
            }}
            disabled={checkoutState === 'loading' || !savedCounty || !accountReady}
            className='mt-8 w-full rounded-2xl bg-primary px-8 py-4 text-lg font-semibold text-white disabled:opacity-50'
          >
            {checkoutState === 'loading' ? 'Opening checkout…' : 'Prepare my forms'}
          </button>
          {!savedCounty ? (
            <p className='mt-3 text-center text-sm font-medium text-red-600' style={nunito}>
              Go back to Your state and add the county you will file in.
            </p>
          ) : null}
          {!accountReady ? (
            <p className='mt-3 text-center text-sm text-gray-500' style={nunito}>
              Verify your email above before payment.
            </p>
          ) : null}
          {checkoutState === 'error' ? (
            <p className='mt-3 text-center text-sm font-medium text-red-600' style={nunito}>
              {checkoutError ??
                'Checkout isn’t available right now. Your answers are saved; try again shortly.'}
            </p>
          ) : null}
        </div>
      ) : (
        <div className='rounded-xl border border-gray-200 bg-white p-6'>
          <h3 className='text-lg font-bold text-gray-900' style={nunito}>
            Form prep for {jurisdiction?.name ?? 'this state'} is not open yet
          </h3>
          <p className='mt-2 text-gray-600' style={nunito}>
            Your answers are saved. Harbor will prepare official forms here once
            this state’s kit is reviewed.
          </p>
        </div>
      )}
    </div>
  );
}
